// UX Redesign: Modern Conversation Page
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiUrl } from '../config/api';
import BuyUnitsModal from '../components/BuyUnitsModal';

// Custom styles
const customStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=DM+Sans:wght@400;500&display=swap');
  
  @keyframes pulseGlow {
    0% { box-shadow: 0 0 5px var(--glow-color), 0 0 10px var(--glow-color); }
    50% { box-shadow: 0 0 15px var(--glow-color), 0 0 25px var(--glow-color); }
    100% { box-shadow: 0 0 5px var(--glow-color), 0 0 10px var(--glow-color); }
  }
  
  @keyframes newMessageGlow {
    0% { box-shadow: 0 0 10px var(--glow-color), 0 0 20px var(--glow-color); opacity: 1; }
    70% { box-shadow: 0 0 10px var(--glow-color), 0 0 20px var(--glow-color); opacity: 1; }
    100% { box-shadow: none; opacity: 1; }
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  .thinking-pulse {
    animation: pulseGlow 2s ease-in-out infinite;
  }
  
  .new-message {
    animation: newMessageGlow 3s ease-out forwards;
  }
  
  .fade-in-up {
    animation: fadeInUp 0.3s ease-out forwards;
  }
`;

export default function Conversation() {
  const { id } = useParams();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showBuyUnits, setShowBuyUnits] = useState(false);
  const [currentThinking, setCurrentThinking] = useState(null);
  const [autonomous, setAutonomous] = useState(false);
  const [paused, setPaused] = useState(false);
  const [userInterjection, setUserInterjection] = useState('');
  const [showInterjection, setShowInterjection] = useState(false);
  const [summary, setSummary] = useState(null);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [consensus, setConsensus] = useState(null);
  const [generatingConsensus, setGeneratingConsensus] = useState(false);
  const [autoCycleCount, setAutoCycleCount] = useState(0);
  const [maxAutoCycles, setMaxAutoCycles] = useState(3);
  const [viewMode, setViewMode] = useState('timeline');
  const [newMessageIds, setNewMessageIds] = useState(new Set());
  const stopRef = useRef(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const aiColors = {
    claude: '#667eea',
    gpt: '#11998e',
    grok: '#eb3349',
    deepseek: '#f093fb',
    user: '#f59e0b'
  };

  const aiIcons = {
    claude: '🧠',
    gpt: '✨',
    grok: '⚡',
    deepseek: '🔮',
    user: '👤'
  };

  const aiNames = {
    claude: 'Claude',
    gpt: 'GPT-4o',
    grok: 'Grok',
    deepseek: 'DeepSeek',
    user: 'You'
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = customStyles;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    loadConversation();
  }, [id]);

  useEffect(() => {
    if (autonomous && !paused && !processing && !stopRef.current) {
      if (autoCycleCount >= maxAutoCycles) {
        setAutonomous(false);
        setAutoCycleCount(0);
        return;
      }
      const timer = setTimeout(() => {
        setAutoCycleCount(prev => prev + 1);
        handleContinue();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [autonomous, paused, processing, messages, autoCycleCount]);

  useEffect(() => {
    if (newMessageIds.size > 0) {
      const timer = setTimeout(() => setNewMessageIds(new Set()), 3000);
      return () => clearTimeout(timer);
    }
  }, [newMessageIds]);

  const loadConversation = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(apiUrl(`/api/conversations/${id}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.conversation) {
        setConversation(data.conversation);
        setMessages(data.messages || []);
      }
    } catch (e) {
      console.error('Failed to load conversation');
    }
    setLoading(false);
  };

  const addNewMessage = (msg) => {
    setMessages(prev => [...prev, msg]);
    setNewMessageIds(prev => new Set([...prev, msg.id]));
  };

  const handleContinue = async (userMessage = null) => {
    if (processing) return;
    setProcessing(true);
    stopRef.current = false;
    const token = localStorage.getItem('token');

    try {
      const res = await fetch(apiUrl(`/api/conversations/${id}/continue`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userMessage })
      });

      
      // Check for insufficient units
      if (res.status === 402) {
        setShowBuyUnits(true);
        setProcessing(false);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done || stopRef.current) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'thinking') {
                setCurrentThinking(`${aiNames[data.ai] || data.ai} is thinking... (${data.position}/${data.total})`);
              } else if (data.type === 'user_message') {
                const userMsg = {
                  id: Date.now() + Math.random(),
                  role: 'user',
                  content: data.content,
                  round_number: data.roundNumber,
                  input_tokens: 0,
                  output_tokens: 0,
                  cost: 0
                };
                addNewMessage(userMsg);
              } else if (data.type === 'response') {
                setCurrentThinking(null);
                const newMessage = {
                  id: Date.now() + Math.random(),
                  role: data.ai,
                  content: data.content,
                  round_number: conversation.rounds_completed + 1,
                  input_tokens: data.tokens || 0,
                  output_tokens: 0,
                  cost: data.cost || 0
                };
                addNewMessage(newMessage);
              } else if (data.type === 'complete') {
                setConversation(prev => ({
                  ...prev,
                  rounds_completed: data.roundNumber,
                  total_cost: data.totalCost
                }));
              } else if (data.type === 'error') {
                alert(data.message);
                setAutonomous(false);
              }
            } catch (e) {
              console.error('Parse error:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Continue error:', error);
      setAutonomous(false);
    }
    
    setProcessing(false);
    setCurrentThinking(null);
  };

  const handlePause = () => {
    setPaused(true);
    setShowInterjection(true);
  };

  const handleResume = () => {
    setPaused(false);
    setShowInterjection(false);
    setUserInterjection('');
  };

  const handleInterjection = () => {
    if (userInterjection.trim()) {
      handleContinue(userInterjection);
      setUserInterjection('');
      setShowInterjection(false);
      setPaused(false);
    }
  };

  const handleStop = () => {
    stopRef.current = true;
    setAutonomous(false);
    setPaused(false);
    setProcessing(false);
    setCurrentThinking(null);
    setAutoCycleCount(0);
  };

  const handleStart = () => {
    stopRef.current = false;
    setAutoCycleCount(0);
    setAutonomous(true);
  };

  const handleSummarize = async () => {
    setGeneratingSummary(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(apiUrl(`/api/conversations/${id}/summary`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.summary) setSummary(data.summary);
    } catch (error) {
      alert('Failed to generate summary');
    }
    setGeneratingSummary(false);
  };

  const handleConsensus = async () => {
    setGeneratingConsensus(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(apiUrl(`/api/conversations/${id}/consensus`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.consensus) setConsensus(data.consensus);
      else if (data.error) alert(data.error);
    } catch (error) {
      alert('Failed to generate consensus analysis');
    }
    setGeneratingConsensus(false);
  };

  const handleDownloadTranscript = async () => {
    let transcript = `# BrainForge Conversation\n`;
    transcript += `Date: ${new Date().toLocaleDateString()}\n`;
    transcript += `Rounds: ${conversation?.rounds_completed || 0}\n\n`;
    transcript += `## Original Prompt\n${conversation?.initial_task || ''}\n\n`;
    transcript += `## Conversation\n\n`;
    
    messages.forEach(msg => {
      const name = msg.role === 'user' ? 'YOUR REDIRECT' : (aiNames[msg.role] || msg.role.toUpperCase());
      transcript += `### ${name} (Round ${msg.round_number})\n${msg.content}\n\n`;
    });

    const blob = new Blob([transcript], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversation-${id}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  // Render message card
  const renderMessage = (msg, index) => {
    const color = aiColors[msg.role] || '#667eea';
    const icon = aiIcons[msg.role] || '🤖';
    const name = msg.role === 'user' ? 'Your Redirect' : (aiNames[msg.role] || msg.role);
    const isNew = newMessageIds.has(msg.id);

    return (
      <div
        key={msg.id || index}
        className={`fade-in-up ${isNew ? 'new-message' : ''}`}
        style={{
          '--glow-color': color,
          background: 'rgba(255,255,255,0.03)',
          border: `1px solid ${msg.role === 'user' ? `${color}40` : 'rgba(255,255,255,0.06)'}`,
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '16px',
          borderLeft: `3px solid ${color}`
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '12px'
        }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            background: `${color}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px'
          }}>
            {icon}
          </div>
          <div>
            <div style={{ color: 'white', fontWeight: '600', fontSize: '14px' }}>{name}</div>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
              Round {msg.round_number} • {(msg.input_tokens || 0) + (msg.output_tokens || 0)} tokens
            </div>
          </div>
        </div>
        <div style={{
          color: 'rgba(255,255,255,0.85)',
          fontSize: '14px',
          lineHeight: '1.7',
          whiteSpace: 'pre-wrap'
        }}>
          {msg.content}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0f1c 0%, #0d1525 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '3px solid rgba(102, 126, 234, 0.3)',
            borderTopColor: '#667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <div style={{ color: 'rgba(255,255,255,0.6)' }}>Loading conversation...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0f1c 0%, #0d1525 100%)',
      fontFamily: "'DM Sans', sans-serif"
    }}>
      {/* Header */}
      <div style={{
        background: 'rgba(255,255,255,0.02)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        padding: '16px 32px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backdropFilter: 'blur(10px)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Link to="/dashboard" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: 'rgba(255,255,255,0.6)',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              <span>←</span> Back
            </Link>
            <div style={{
              width: '1px',
              height: '24px',
              background: 'rgba(255,255,255,0.1)'
            }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px'
              }}>
                ⚡
              </div>
              <span style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: '16px',
                fontWeight: '600',
                color: 'white'
              }}>
                BrainForge
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              background: 'rgba(255,255,255,0.05)',
              padding: '8px 16px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '13px'
            }}>
              <span style={{ color: 'rgba(255,255,255,0.5)' }}>Round</span>
              <span style={{ color: '#667eea', fontWeight: '600' }}>{conversation?.rounds_completed || 0}</span>
              <span style={{ color: 'rgba(255,255,255,0.3)' }}>|</span>
              <span style={{ color: 'rgba(255,255,255,0.5)' }}>Units</span>
              <span style={{ color: '#22c55e', fontWeight: '600' }}>{messages.length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '32px'
      }}>
        {/* Original Prompt Card */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '16px'
          }}>
            <span style={{ fontSize: '20px' }}>📝</span>
            <span style={{
              fontFamily: "'Outfit', sans-serif",
              fontSize: '16px',
              fontWeight: '600',
              color: 'white'
            }}>Original Prompt</span>
          </div>
          <div style={{
            color: 'rgba(255,255,255,0.8)',
            fontSize: '15px',
            lineHeight: '1.7',
            padding: '16px',
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '12px'
          }}>
            {conversation?.initial_task || 'No prompt available'}
          </div>
        </div>

        {/* User Redirects */}
        {messages.filter(m => m.role === 'user').length > 0 && (
          <div style={{
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '16px'
            }}>
              <span style={{ fontSize: '20px' }}>👤</span>
              <span style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: '16px',
                fontWeight: '600',
                color: '#f59e0b'
              }}>Your Redirects</span>
            </div>
            {messages.filter(m => m.role === 'user').map((msg, idx) => (
              <div key={msg.id || idx} style={{
                padding: '12px 16px',
                background: 'rgba(0,0,0,0.2)',
                borderRadius: '10px',
                marginBottom: idx < messages.filter(m => m.role === 'user').length - 1 ? '10px' : '0'
              }}>
                <div style={{ fontSize: '11px', color: '#f59e0b', marginBottom: '6px', fontWeight: '600' }}>
                  Round {msg.round_number}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>
                  {msg.content}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Control Panel */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            {/* Autonomous Toggle */}
            <button
              onClick={autonomous ? handleStop : handleStart}
              disabled={processing && !autonomous}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                background: autonomous ? 'rgba(239, 68, 68, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                border: `1px solid ${autonomous ? '#ef4444' : '#22c55e'}`,
                borderRadius: '10px',
                color: autonomous ? '#ef4444' : '#22c55e',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              {autonomous ? '⏹️ Stop Auto' : '🚀 Auto Mode'}
            </button>

            {/* Cycle Count Selector - only show when not in autonomous mode */}
            {!autonomous && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                background: 'rgba(102, 126, 234, 0.1)',
                border: '1px solid rgba(102, 126, 234, 0.3)',
                borderRadius: '10px'
              }}>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>Cycles:</span>
                <select
                  value={maxAutoCycles}
                  onChange={(e) => setMaxAutoCycles(parseInt(e.target.value))}
                  style={{
                    background: 'rgba(0,0,0,0.3)',
                    border: '1px solid rgba(102, 126, 234, 0.4)',
                    borderRadius: '6px',
                    color: '#667eea',
                    fontSize: '14px',
                    fontWeight: '600',
                    padding: '4px 8px',
                    cursor: 'pointer',
                    outline: 'none'
                  }}
                >
                  <option value={2} style={{ background: '#1a1a2e' }}>2</option>
                  <option value={3} style={{ background: '#1a1a2e' }}>3</option>
                  <option value={4} style={{ background: '#1a1a2e' }}>4</option>
                  <option value={5} style={{ background: '#1a1a2e' }}>5</option>
                </select>
              </div>
            )}

            {autonomous && (
              <span style={{
                color: '#fbbf24',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                ⚠️ Cycle {autoCycleCount}/{maxAutoCycles}
              </span>
            )}

            {autonomous && !paused && (
              <button onClick={handlePause} style={{
                padding: '10px 20px',
                background: 'rgba(245, 158, 11, 0.2)',
                border: '1px solid #f59e0b',
                borderRadius: '10px',
                color: '#f59e0b',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}>
                ⏸️ Pause
              </button>
            )}

            {paused && (
              <button onClick={handleResume} style={{
                padding: '10px 20px',
                background: 'rgba(34, 197, 94, 0.2)',
                border: '1px solid #22c55e',
                borderRadius: '10px',
                color: '#22c55e',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}>
                ▶️ Resume
              </button>
            )}

            <div style={{
              width: '1px',
              height: '30px',
              background: 'rgba(255,255,255,0.1)',
              margin: '0 8px'
            }} />

            <button
              onClick={handleSummarize}
              disabled={generatingSummary}
              style={{
                padding: '10px 20px',
                background: 'rgba(139, 92, 246, 0.2)',
                border: '1px solid #8b5cf6',
                borderRadius: '10px',
                color: '#8b5cf6',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              {generatingSummary ? '⏳...' : '📝 Summarize'}
            </button>

            <button
              onClick={handleConsensus}
              disabled={generatingConsensus}
              style={{
                padding: '10px 20px',
                background: 'rgba(16, 185, 129, 0.2)',
                border: '1px solid #10b981',
                borderRadius: '10px',
                color: '#10b981',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              {generatingConsensus ? '⏳...' : '🤝 Consensus'}
            </button>

            <button onClick={handleDownloadTranscript} style={{
              padding: '10px 20px',
              background: 'rgba(59, 130, 246, 0.2)',
              border: '1px solid #3b82f6',
              borderRadius: '10px',
              color: '#3b82f6',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}>
              💾 Download
            </button>
          </div>

          {autonomous && (
            <div style={{
              marginTop: '16px',
              padding: '12px 16px',
              background: 'rgba(251, 191, 36, 0.1)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span>⚠️</span>
              <span style={{ color: '#fbbf24', fontSize: '13px' }}>
                <strong>Autonomous Mode:</strong> Will run for {maxAutoCycles} round{maxAutoCycles > 1 ? 's' : ''} then pause. Click Stop anytime.
              </span>
            </div>
          )}
        </div>

        {/* User Redirect Input Panel */}
        {!processing && !autonomous && messages.length > 0 && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(251, 191, 36, 0.1))',
            border: '2px solid rgba(245, 158, 11, 0.4)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            {!showInterjection ? (
              <>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '12px'
                }}>
                  <span style={{ fontSize: '24px' }}>💡</span>
                  <span style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#fbbf24'
                  }}>What's Next?</span>
                </div>
                <p style={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '14px',
                  marginBottom: '20px'
                }}>
                  Redirect the conversation or let your AI team continue collaborating.
                </p>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setShowInterjection(true)}
                    style={{
                      padding: '14px 28px',
                      background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                      border: 'none',
                      borderRadius: '12px',
                      color: 'white',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)'
                    }}
                  >
                    ✋ Add My Input
                  </button>
                  <button
                    onClick={() => handleContinue()}
                    disabled={processing}
                    style={{
                      padding: '14px 28px',
                      background: 'rgba(34, 197, 94, 0.2)',
                      border: '2px solid #22c55e',
                      borderRadius: '12px',
                      color: '#22c55e',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    ➡️ Just Continue
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '12px'
                }}>
                  <span style={{ fontSize: '24px' }}>✏️</span>
                  <span style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#fbbf24'
                  }}>Redirect Your Dream Team</span>
                </div>
                <textarea
                  value={userInterjection}
                  onChange={(e) => setUserInterjection(e.target.value)}
                  placeholder="e.g., 'Focus more on the budget constraints...' or 'Let's pivot to discussing the marketing angle...'"
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    padding: '16px',
                    background: 'rgba(0,0,0,0.3)',
                    border: '2px solid rgba(245, 158, 11, 0.3)',
                    borderRadius: '12px',
                    color: 'white',
                    fontSize: '15px',
                    lineHeight: '1.6',
                    resize: 'vertical',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#f59e0b'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(245, 158, 11, 0.3)'}
                  autoFocus
                />
                <div style={{ marginTop: '16px', display: 'flex', gap: '12px' }}>
                  <button
                    onClick={handleInterjection}
                    disabled={!userInterjection.trim()}
                    style={{
                      padding: '14px 28px',
                      background: userInterjection.trim() ? 'linear-gradient(135deg, #22c55e, #16a34a)' : 'rgba(255,255,255,0.1)',
                      border: 'none',
                      borderRadius: '12px',
                      color: userInterjection.trim() ? 'white' : 'rgba(255,255,255,0.4)',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: userInterjection.trim() ? 'pointer' : 'not-allowed'
                    }}
                  >
                    🚀 Continue with My Input
                  </button>
                  <button
                    onClick={() => {
                      setShowInterjection(false);
                      setUserInterjection('');
                    }}
                    style={{
                      padding: '14px 28px',
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '12px',
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '15px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Thinking Indicator */}
        {currentThinking && (
          <div className="thinking-pulse" style={{
            '--glow-color': '#667eea',
            background: 'rgba(102, 126, 234, 0.15)',
            border: '1px solid rgba(102, 126, 234, 0.3)',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              border: '3px solid rgba(102, 126, 234, 0.3)',
              borderTopColor: '#667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <span style={{ color: '#667eea', fontWeight: '600', fontSize: '16px' }}>
              🤖 {currentThinking}
            </span>
          </div>
        )}

        {/* Summary */}
        {summary && (
          <div style={{
            background: 'rgba(139, 92, 246, 0.15)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '16px'
            }}>
              <span style={{ fontSize: '20px' }}>📝</span>
              <span style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: '16px',
                fontWeight: '600',
                color: '#8b5cf6'
              }}>AI Summary</span>
            </div>
            <div style={{
              color: 'rgba(255,255,255,0.85)',
              fontSize: '15px',
              lineHeight: '1.7',
              whiteSpace: 'pre-wrap'
            }}>
              {summary}
            </div>
          </div>
        )}

        {/* Consensus Analysis */}
        {consensus && (
          <div style={{
            background: 'rgba(16, 185, 129, 0.15)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '16px'
            }}>
              <span style={{ fontSize: '20px' }}>🤝</span>
              <span style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: '16px',
                fontWeight: '600',
                color: '#10b981'
              }}>Consensus Analysis</span>
              <span style={{
                marginLeft: 'auto',
                fontSize: '12px',
                color: 'rgba(255,255,255,0.5)'
              }}>Points of agreement & disagreement across your AI Dream Team</span>
            </div>
            <div style={{
              color: 'rgba(255,255,255,0.85)',
              fontSize: '15px',
              lineHeight: '1.7',
              whiteSpace: 'pre-wrap'
            }}>
              {consensus}
            </div>
          </div>
        )}

        {/* Messages */}
        <div style={{ marginTop: '24px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '20px'
          }}>
            <h2 style={{
              fontFamily: "'Outfit', sans-serif",
              fontSize: '18px',
              fontWeight: '600',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span>💬</span> AI Responses
            </h2>
            <div style={{
              color: 'rgba(255,255,255,0.4)',
              fontSize: '13px'
            }}>
              {messages.filter(m => m.role !== 'user').length} messages
            </div>
          </div>

          {messages.filter(m => m.role !== 'user').length === 0 ? (
            <div style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '16px',
              padding: '60px 40px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🤖</div>
              <p style={{ color: 'rgba(255,255,255,0.5)' }}>
                Your AI team is ready to respond. Click "Just Continue" to start!
              </p>
            </div>
          ) : (
            messages.filter(m => m.role !== 'user').map((msg, index) => renderMessage(msg, index))
          )}
        </div>
      </div>
      
      {/* Buy Units Modal */}
      {showBuyUnits && (
        <BuyUnitsModal
          onClose={() => setShowBuyUnits(false)}
          currentBalance={0}
        />
      )}
    </div>
  );
}
